<template>
  <div>
    <p-modal
      ref="modal"
      label="Attached Documents"
      :name="modalId"
      size="lg">
      <template v-slot:modal-footer>
        <div class="w-100 d-flex justify-content-end">
          <p-button variant="secondary" @click="hide">Close</p-button>
        </div>
      </template>
      <template v-slot:default>
        <div>
          <div v-if="canUploadOrDelete" class="mb-4">
            <attachment
              ref="fileUploader"
              :accepted-file-types="acceptedFileTypes"
              :max-file-size-mb="maxFileSizeMb"
              @file-added="onFileAdded"
              @file-removed="onFileRemoved"
            />
          </div>

          <b-table
            :items="documents"
            :fields="fields"
            responsive
            bordered
            hover
            small
            show-empty
            empty-text="No documents attached.">
          <template v-slot:cell(fileName)="{ item }">
            {{ item.fileName }}
          </template>

          <template v-slot:cell(actions)="{ item }">
            <div class="d-flex">
              <p-button
                class="mr-1"
                variant="outline-secondary"
                size="sm"
                @click="onDownload(item)">
                <i class="bi bi-download"></i>
              </p-button>
              <p-button
                v-if="canUploadOrDelete"
                variant="outline-danger"
                size="sm"
                @click="onDelete(item)">
                <i class="bi bi-trash"></i>
              </p-button>
            </div>
          </template>
        </b-table>
        </div>
      </template>
    </p-modal>
  </div>
</template>

<script>
import uniqueid from "lodash.uniqueid";
import Attachment from "@/components/Attachment.vue";
import { datetimeToLocalTimeFormatter } from "@/components/Common/Formatters";
import selectListOptionsDataContext from "@/services/selectListOptions.dataContext.js";
import { useCurrentUserStore } from "@/stores/currentUser.js";

export default {
  name: "attachment-modal",
  components: {
    Attachment
  },
  props: {
    documents: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      modalId: uniqueid("attachmentModal_"),
      acceptedFileTypes: null,
      authorizedRoles: [],
      maxFileSizeMb: 10,
      fields: [
        { key: "fileName", label: "File Name", sortable: true },
        { key: "userName", label: "Uploaded By", sortable: true },
        { key: "addDate", label: "Uploaded On", sortable: true, formatter: datetimeToLocalTimeFormatter },
        { key: "fileSizeInKb", label: "Size (Kb)", sortable: true },
        { key: "actions", label: "", thStyle: { width: "90px" } }
      ]
    };
  },
  computed: {
    canUploadOrDelete() {
      return this.authorizedRoles.some(role => useCurrentUserStore().currentUserHasRole(role));
    }
  },
  methods: {
    async show() {
      const [acceptedFileTypes, authorizedRoles, maxFileSizeMb, locationId] = await Promise.all([
        selectListOptionsDataContext.getStoreDropdownData('attachmentAcceptedFileTypes'),
        selectListOptionsDataContext.getStoreDropdownData('attachmentAuthorizedRoles'),
        selectListOptionsDataContext.getStoreDropdownData('attachmentMaxFileSizeMb'),
        selectListOptionsDataContext.getSelectedLocationIdAsync()
      ]);

      const filteredFileTypes = acceptedFileTypes.filter(t => t.locationId === locationId);
      this.acceptedFileTypes = filteredFileTypes.length > 0 ? filteredFileTypes.map(t => t.value) : null;

      const filteredRoles = authorizedRoles.filter(t => t.locationId === locationId);
      this.authorizedRoles = filteredRoles.map(t => t.value);

      const filteredMaxFileSize = maxFileSizeMb.filter(t => t.locationId === locationId);
      this.maxFileSizeMb = filteredMaxFileSize.length > 0 ? Number(filteredMaxFileSize[0].value) : 10;

      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    removeUploadedFile(file) {
      this.$refs.fileUploader?.removeFile(file);
    },
    onFileAdded(file) {
      this.$emit("upload", file);
    },
    onFileRemoved(file) {
      this.$emit("file-removed", file);
    },
    onDownload(document) {
      this.$emit("download", document);
    },
    onDelete(document) {
      this.$emit("delete", document);
    }
  }
};
</script>

<style scoped lang="scss">
</style>
